import { useEffect } from 'react';

const STORAGE_KEY = 'pylon-bubble-position';
const BUBBLE_CLASS = 'PylonChat-bubbleFrameContainer';
const DRAG_THRESHOLD = 5;

interface Position {
  x: number;
  y: number;
}

function getStoredPosition(): Position | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function savePosition(pos: Position): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Creates a corner-mounted drag handle with a grip icon.
 * Positioned at top-left, always subtly visible, more prominent on hover.
 */
function createDragHandle(): HTMLElement {
  const handle = document.createElement('div');
  handle.className = 'pylon-drag-handle';

  // 4-dot grip pattern (2x2) - cleaner than 6 dots for small size
  handle.innerHTML = `
    <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
      <circle cx="3" cy="3" r="1.2"/>
      <circle cx="7" cy="3" r="1.2"/>
      <circle cx="3" cy="7" r="1.2"/>
      <circle cx="7" cy="7" r="1.2"/>
    </svg>
  `;

  Object.assign(handle.style, {
    position: 'absolute',
    top: '-8px',
    left: '-8px',
    width: '22px',
    height: '22px',
    background: '#1f2937',
    borderRadius: '50%',
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9ca3af',
    opacity: '0.35',
    transition: 'opacity 0.2s, transform 0.2s, box-shadow 0.2s',
    zIndex: '2147483647',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    border: '1px solid rgba(255,255,255,0.1)',
  });

  return handle;
}

/**
 * Makes the Pylon chat bubble draggable via a corner grip handle.
 *
 * Design principles (web-optimized):
 * - Corner-mounted handle (top-left) - doesn't interfere with chat click
 * - Always subtly visible (35% opacity) - discoverable without hover
 * - More prominent on hover (100% opacity) - clear affordance
 * - Familiar 4-dot grip pattern - universal drag indicator
 * - Position persists to localStorage
 */
export function usePylonDraggable(): void {
  useEffect(() => {
    let bubble: HTMLElement | null = null;
    let handle: HTMLElement | null = null;
    let isDragging = false;
    let hasDragged = false;
    let startX = 0;
    let startY = 0;
    let initialX = 0;
    let initialY = 0;
    let position: Position = getStoredPosition() ?? { x: 0, y: 0 };

    const applyPosition = (): void => {
      if (bubble) {
        bubble.style.transform = `translate(${position.x}px, ${position.y}px)`;
      }
    };

    const setHandleIdle = (): void => {
      if (handle) {
        handle.style.opacity = '0.35';
        handle.style.transform = 'scale(1)';
        handle.style.cursor = 'grab';
        handle.style.boxShadow = '0 1px 3px rgba(0,0,0,0.2)';
      }
    };

    const setHandleHover = (): void => {
      if (handle && !isDragging) {
        handle.style.opacity = '1';
        handle.style.transform = 'scale(1.1)';
        handle.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
      }
    };

    const setHandleDragging = (): void => {
      if (handle) {
        handle.style.opacity = '1';
        handle.style.transform = 'scale(1.15)';
        handle.style.cursor = 'grabbing';
        handle.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)';
      }
    };

    const onMouseEnter = (): void => {
      if (!isDragging) {
        setHandleHover();
      }
    };

    const onMouseLeave = (): void => {
      if (!isDragging) {
        setHandleIdle();
      }
    };

    const onMouseDown = (e: MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      isDragging = true;
      hasDragged = false;
      startX = e.clientX;
      startY = e.clientY;
      initialX = position.x;
      initialY = position.y;
      setHandleDragging();
    };

    const onMouseMove = (e: MouseEvent): void => {
      if (!isDragging) {
        return;
      }
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      if (!hasDragged && Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) {
        return;
      }
      hasDragged = true;
      position = { x: initialX + deltaX, y: initialY + deltaY };
      applyPosition();
    };

    const onMouseUp = (): void => {
      if (!isDragging) {
        return;
      }
      isDragging = false;
      setHandleIdle();
      if (hasDragged) {
        savePosition(position);
      }
    };

    // Touch handlers
    const onTouchStart = (e: TouchEvent): void => {
      if (e.touches.length !== 1) {
        return;
      }
      e.preventDefault();
      isDragging = true;
      hasDragged = false;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      initialX = position.x;
      initialY = position.y;
      setHandleDragging();
    };

    const onTouchMove = (e: TouchEvent): void => {
      if (!isDragging || e.touches.length !== 1) {
        return;
      }
      const deltaX = e.touches[0].clientX - startX;
      const deltaY = e.touches[0].clientY - startY;

      if (!hasDragged && Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) {
        return;
      }
      hasDragged = true;
      e.preventDefault();
      position = { x: initialX + deltaX, y: initialY + deltaY };
      applyPosition();
    };

    const onTouchEnd = (): void => {
      if (!isDragging) {
        return;
      }
      isDragging = false;
      setHandleIdle();
      if (hasDragged) {
        savePosition(position);
      }
    };

    const setupBubble = (el: HTMLElement): void => {
      if (bubble === el) {
        return;
      }
      bubble = el;

      // Ensure bubble can hold the handle and receive transforms
      el.style.overflow = 'visible';
      applyPosition();

      // Create and attach drag handle
      handle = createDragHandle();
      el.appendChild(handle);

      // Handle interactions
      handle.addEventListener('mouseenter', onMouseEnter);
      handle.addEventListener('mouseleave', onMouseLeave);
      handle.addEventListener('mousedown', onMouseDown);
      handle.addEventListener('touchstart', onTouchStart, { passive: false });
    };

    const cleanupBubble = (): void => {
      if (handle) {
        handle.removeEventListener('mouseenter', onMouseEnter);
        handle.removeEventListener('mouseleave', onMouseLeave);
        handle.removeEventListener('mousedown', onMouseDown);
        handle.removeEventListener('touchstart', onTouchStart);
        handle.remove();
        handle = null;
      }
    };

    // Watch for Pylon bubble appearing in DOM
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) {
            continue;
          }
          if (node.classList.contains(BUBBLE_CLASS)) {
            setupBubble(node);
          } else {
            const found = node.querySelector<HTMLElement>(`.${BUBBLE_CLASS}`);
            if (found) {
              setupBubble(found);
            }
          }
        }
      }
    });

    // Check if already exists
    const existing = document.querySelector<HTMLElement>(`.${BUBBLE_CLASS}`);
    if (existing) {
      setupBubble(existing);
    }

    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);

    return () => {
      observer.disconnect();
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      cleanupBubble();
    };
  }, []);
}
